'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Package, ArrowLeft, Plus, Search, Eye, CheckCircle2, X, Download, Truck,  } from 'lucide-react';
import { MOCK_GRNS, MOCK_PURCHASE_ORDERS, GRN_STATUS_STYLES, GoodsReceivedNote, GRNStatus, FARMS_LIST,  } from '../../components/procurementData';
import { useRole } from '@/lib/useRole';

function GRNStatusBadge({ status }: { status: GRNStatus }) {
  const st = GRN_STATUS_STYLES[status];
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>;
}

function GRNDrawer({ grn, onClose, onApprove }: { grn: GoodsReceivedNote; onClose: () => void; onApprove: (id: string) => void }) {
  const totalAccepted = grn.items.reduce((s, i) => s + i.acceptedQty, 0);
  const totalRejected = grn.items.reduce((s, i) => s + i.rejectedQty, 0);
  const totalDelivered = grn.items.reduce((s, i) => s + i.deliveredQty, 0);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{grn.grnNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{grn.supplier} · {grn.receiptDate}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <GRNStatusBadge status={grn.status} />
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">PO: {grn.poNumber}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Farm', value: grn.farm },
              { label: 'Store / Warehouse', value: grn.store },
              { label: 'Received By', value: grn.receivedBy },
              { label: 'Inspected By', value: grn.inspectedBy },
              { label: 'Delivery Note #', value: grn.deliveryNoteNumber },
              { label: 'Vehicle / Ref', value: grn.vehicleRef || '—' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-blue-700">{totalDelivered}</div>
              <div className="text-xs text-blue-500 mt-0.5">Delivered</div>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-green-700">{totalAccepted}</div>
              <div className="text-xs text-green-500 mt-0.5">Accepted</div>
            </div>
            <div className="bg-red-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-red-600">{totalRejected}</div>
              <div className="text-xs text-red-400 mt-0.5">Rejected</div>
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Received Items</div>
            <div className="space-y-3">
              {grn.items.map(item => (
                <div key={item.id} className="bg-gray-50 rounded-xl p-3">
                  <div className="text-sm font-semibold text-gray-800 mb-2">{item.description}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-gray-400">Ordered:</span> <span className="font-semibold">{item.orderedQty} {item.unit}</span></div>
                    <div><span className="text-gray-400">Delivered:</span> <span className="font-semibold">{item.deliveredQty}</span></div>
                    <div><span className="text-gray-400">Accepted:</span> <span className="font-semibold text-green-700">{item.acceptedQty}</span></div>
                    {item.rejectedQty > 0 && <div><span className="text-gray-400">Rejected:</span> <span className="font-semibold text-red-600">{item.rejectedQty}</span></div>}
                    {item.damagedQty > 0 && <div><span className="text-gray-400">Damaged:</span> <span className="font-semibold text-orange-600">{item.damagedQty}</span></div>}
                    {item.batchNumber && <div><span className="text-gray-400">Batch:</span> <span className="font-semibold">{item.batchNumber}</span></div>}
                    {item.expiryDate && <div><span className="text-gray-400">Expiry:</span> <span className="font-semibold">{item.expiryDate}</span></div>}
                  </div>
                  {item.remarks && <div className="text-xs text-gray-500 mt-2 italic">{item.remarks}</div>}
                  {/* Delivery progress bar */}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Delivery progress</span>
                      <span>{Math.round((item.deliveredQty / item.orderedQty) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${Math.min((item.deliveredQty / item.orderedQty) * 100, 100)}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {grn.notes && (
            <div>
              <div className="text-xs font-bold text-gray-700 mb-1">Notes</div>
              <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{grn.notes}</div>
            </div>
          )}

          {grn.status === 'approved' && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-3">
              <CheckCircle2 size={15} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-green-700">
                <strong>GRN Approved.</strong> Accepted quantities have been added to inventory stock. Stock movement created automatically.
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Download size={13} /> Export
          </button>
          {grn.status === 'pending_inspection' && (
            <button onClick={() => { onApprove(grn.id); onClose(); }} className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-xl hover:bg-green-700 transition-colors">
              <CheckCircle2 size={13} /> Approve GRN & Update Inventory
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── New GRN Form ─────────────────────────────────────────────────────────────
function NewGRNForm({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [form, setForm] = useState({
    poId: '',
    deliveryNoteNumber: '',
    receiptDate: new Date().toISOString().split('T')[0],
    farm: 'Idudi Poultry Farm',
    store: 'Main Store',
    receivedBy: 'Okello James',
    inspectedBy: '',
    vehicleRef: '',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const selectedPO = MOCK_PURCHASE_ORDERS.find(p => p.id === form.poId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.poId || !form.deliveryNoteNumber || !form.inspectedBy) return;
    setSubmitted(true);
    setTimeout(() => { onSave(); onClose(); }, 1500);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-bold text-gray-900">Record Goods Receipt</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={28} className="text-green-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">GRN Recorded</h3>
            <p className="text-sm text-gray-500">Goods receipt has been recorded. Inventory will be updated upon approval.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Purchase Order *</label>
              <select value={form.poId} onChange={e => setForm(f => ({ ...f, poId: e.target.value }))} required className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                <option value="">Select purchase order...</option>
                {MOCK_PURCHASE_ORDERS.filter(p => ['approved', 'issued', 'partially_delivered'].includes(p.status)).map(p => (
                  <option key={p.id} value={p.id}>{p.poNumber} — {p.supplier}</option>
                ))}
              </select>
            </div>

            {selectedPO && (
              <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
                <strong>{selectedPO.supplier}</strong> · {selectedPO.items.length} item{selectedPO.items.length !== 1 ? 's' : ''} · Expected: {selectedPO.expectedDeliveryDate}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Delivery Note # *</label>
                <input type="text" value={form.deliveryNoteNumber} onChange={e => setForm(f => ({ ...f, deliveryNoteNumber: e.target.value }))} required placeholder="DN-XXXX-0000" className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Receipt Date *</label>
                <input type="date" value={form.receiptDate} onChange={e => setForm(f => ({ ...f, receiptDate: e.target.value }))} required className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Farm</label>
                <select value={form.farm} onChange={e => setForm(f => ({ ...f, farm: e.target.value }))} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                  {FARMS_LIST.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Store / Warehouse</label>
                <select value={form.store} onChange={e => setForm(f => ({ ...f, store: e.target.value }))} className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white">
                  {['Main Store', 'Feed Store', 'Veterinary Store', 'Equipment Store'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Received By *</label>
                <input type="text" value={form.receivedBy} onChange={e => setForm(f => ({ ...f, receivedBy: e.target.value }))} required className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Inspected By *</label>
                <input type="text" value={form.inspectedBy} onChange={e => setForm(f => ({ ...f, inspectedBy: e.target.value }))} required placeholder="Inspector name..." className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Vehicle / Delivery Reference</label>
                <input type="text" value={form.vehicleRef} onChange={e => setForm(f => ({ ...f, vehicleRef: e.target.value }))} placeholder="e.g. UAX 234K" className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Inspection notes, condition remarks..." className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none" />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">Cancel</button>
              <button type="submit" className="flex-1 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors">Record GRN</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GoodsReceivedContent() {
  const { can } = useRole();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GoodsReceivedNote | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [grns, setGrns] = useState(MOCK_GRNS);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = can('manage_procurement');

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 3500); }

  function handleApprove(id: string) {
    setGrns(prev => prev.map(g => g.id === id ? { ...g, status: 'approved' as GRNStatus } : g));
    showToast('GRN approved. Inventory has been updated with accepted quantities.');
  }

  const filtered = grns.filter(g => {
    const q = search.toLowerCase();
    return !q || g.grnNumber.toLowerCase().includes(q) || g.supplier.toLowerCase().includes(q) || g.poNumber.toLowerCase().includes(q);
  });

  const stats = {
    total: grns.length,
    approved: grns.filter(g => g.status === 'approved').length,
    pending: grns.filter(g => g.status === 'pending_inspection').length,
    totalAccepted: grns.reduce((s, g) => s + g.items.reduce((si, i) => si + i.acceptedQty, 0), 0),
  };

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Goods Received Notes</h1>
          <p className="text-xs text-gray-400 mt-0.5">Record and manage supplier deliveries — Idudi Poultry Farm</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowNewForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700 transition-colors shadow-sm">
            <Plus size={14} /> Record GRN
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 my-5">
        {[
          { label: 'Total GRNs', value: stats.total, color: 'text-gray-900' },
          { label: 'Approved', value: stats.approved, color: 'text-green-600' },
          { label: 'Pending Inspection', value: stats.pending, color: 'text-amber-600' },
          { label: 'Total Units Received', value: stats.totalAccepted.toLocaleString(), color: 'text-teal-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Partial Delivery Alert */}
      {MOCK_PURCHASE_ORDERS.some(p => p.status === 'partially_delivered') && (
        <div className="mb-4 flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-800">
          <Truck size={16} className="flex-shrink-0 text-orange-500" />
          <span><strong>1 partial delivery outstanding</strong> — PO-2026-0125 has 80 bags of Broiler Finisher Feed still pending from Ugachick Poultry Breeders.</span>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search GRNs..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600">
          <Download size={14} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">GRN #</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">PO #</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Store</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Received By</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Accepted</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Rejected</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-400">
                    <Package size={32} className="mx-auto mb-2 text-gray-200" />
                    No goods received notes found.
                  </td>
                </tr>
              ) : filtered.map(grn => {
                const totalAccepted = grn.items.reduce((s, i) => s + i.acceptedQty, 0);
                const totalRejected = grn.items.reduce((s, i) => s + i.rejectedQty, 0);
                const st = GRN_STATUS_STYLES[grn.status];
                return (
                  <tr key={grn.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setSelected(grn)}>
                    <td className="px-4 py-3 font-bold text-xs text-gray-900">{grn.grnNumber}</td>
                    <td className="px-4 py-3 text-xs text-blue-600 font-semibold">{grn.poNumber}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{grn.supplier}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{grn.store}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{grn.receivedBy}</td>
                    <td className="px-4 py-3 text-center text-xs font-bold text-green-700">{totalAccepted}</td>
                    <td className="px-4 py-3 text-center text-xs font-bold text-red-500">{totalRejected || '—'}</td>
                    <td className="px-4 py-3"><GRNStatusBadge status={grn.status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-400">{grn.receiptDate}</td>
                    <td className="px-4 py-3">
                      <button onClick={e => { e.stopPropagation(); setSelected(grn); }} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <Eye size={13} className="text-gray-500" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">{filtered.length} of {grns.length} GRNs</span>
        </div>
      </div>

      {selected && <GRNDrawer grn={selected} onClose={() => setSelected(null)} onApprove={handleApprove} />}
      {showNewForm && <NewGRNForm onClose={() => setShowNewForm(false)} onSave={() => { showToast('GRN recorded successfully. Pending inspection.'); setShowNewForm(false); }} />}
    </div>
  );
}
